import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * How to put one website widget on a site (plan-004 WC-81, FR-WC-16, design §17.8.1, §17.10.5).
 *
 * Everything an owner needs without leaving the page: the snippet with Copy and "Copied", where to
 * paste it, what each `data-*` attribute does (the three the snippet carries with values, and the
 * three optional ones it leaves out), the Content-Security-Policy lines, how to check it works, and
 * what it costs — the API's billing sentence, at the account's own rate.
 *
 * THE SNIPPET IS THE API'S. It is composed on the runtime's `rtc` host with the required attributes
 * (DD4-19); the console prints it as it came and never composes a tag of its own (review #176). The
 * widget key in it is public by construction and is shown in full.
 */
import { Button } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import type { WebChatInstallView, WebsiteWidgetView } from "../../api/slice/messaging.js";

/** How long "Copied" stays on the button before it reads "Copy snippet" again. */
export const COPIED_FEEDBACK_MS = 2_000;

/** The attributes, in the order the snippet carries them, then the optional ones. */
const ATTRIBUTE: readonly { readonly name: string; readonly inSnippet: boolean; readonly meaning: string }[] = [
  {
    name: "data-widget-key",
    inSnippet: true,
    get meaning() { return portalText("operations", "which_chat_this_is_it_is_public_it_sits_in_your_page_sourc_86f5a10b"); },
  },
  {
    name: "data-accent",
    inSnippet: true,
    get meaning() { return portalText("operations", "the_colour_of_the_bubble_the_header_and_the_send_button_as_90c9b242"); },
  },
  {
    name: "data-position",
    inSnippet: true,
    get meaning() { return portalText("operations", "right_or_left_the_bottom_corner_the_bubble_sits_in_6a5b6ca5"); },
  },
  {
    name: "data-page",
    inSnippet: false,
    get meaning() { return portalText("operations", "optional_paths_separated_by_commas_such_as_menu_order_the_ee15a9a2"); },
  },
  {
    name: "data-privacy-url",
    inSnippet: false,
    get meaning() { return portalText("operations", "optional_an_https_address_of_your_privacy_notice_the_chat_0d9005f0"); },
  },
  {
    name: "data-language",
    inSnippet: false,
    get meaning() { return portalText("operations", "optional_the_chat_s_language_such_as_el_or_en_left_out_it_542057b2"); },
  },
];

export interface InstallGuideProps {
  readonly widget: WebsiteWidgetView;
  readonly install: WebChatInstallView;
}

export function InstallGuide({ widget, install }: InstallGuideProps): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState<"copied" | "refused" | null>(null);

  useEffect(() => {
    if (copied !== "copied") return undefined;
    const handle = globalThis.setTimeout(() => {
      setCopied(null);
    }, COPIED_FEEDBACK_MS);
    return () => {
      globalThis.clearTimeout(handle);
    };
  }, [copied]);

  const copySnippet = (): void => {
    const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      setCopied("refused");
      return;
    }
    clipboard.writeText(widget.snippet).then(
      () => {
        setCopied("copied");
      },
      () => {
        setCopied("refused");
      },
    );
  };

  const policy = widget.voiceEnabled
    ? [...widget.contentSecurityPolicy.text, ...widget.contentSecurityPolicy.voice]
    : widget.contentSecurityPolicy.text;

  return (
    <div className="mg-install">
      <p className="mg-section-title">{portalText("operations", "put_it_on_your_site_297a5e90")}</p>
      <p className="mg-card-note">
        {portalText("operations", "paste_this_before_the_closing_cff4cb75")}<code className="mg-inline-code">{"</body>"}</code> {portalText("operations", "tag_on_every_page_where_the_chat_should_appear_or_with_a_s_f0b9fb8f")}</p>
      <code className="mg-snippet">
        {widget.snippet}
      </code>
      <div className="mg-card-actions">
        <Button variant="secondary" onClick={copySnippet}>
          {copied === "copied" ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_snippet_9297913e")}
        </Button>
        <span className="mg-card-note" role="status">
          {copied === "copied"
            ? portalText("operations", "the_snippet_is_on_your_clipboard_b3b4c51d")
            : copied === "refused"
              ? portalText("operations", "the_browser_did_not_allow_copying_select_the_snippet_and_c_672fbf3d")
              : ""}
        </span>
      </div>

      <p className="mg-section-title">{portalText("operations", "what_each_part_does_c5fe009e")}</p>
      <dl className="mg-attribute">
        {ATTRIBUTE.map((one) => (
          <div className="mg-attribute-row" key={one.name}>
            <dt className="mg-attribute-name">
              <code className="mg-inline-code">{one.name}</code>
              {one.inSnippet ? null : <span className="mg-soon">{portalText("operations", "not_in_the_snippet_788c1600")}</span>}
            </dt>
            <dd className="mg-attribute-meaning">{one.meaning}</dd>
          </div>
        ))}
      </dl>

      <p className="mg-section-title">{portalText("operations", "if_your_site_has_a_content_security_policy_353b94bc")}</p>
      <p className="mg-card-note">
        {portalText("operations", "most_sites_do_not_if_yours_sends_one_add_these_sources_to_adbcd182")}</p>
      <code className="mg-snippet">
        {policy.join(";\n")}
      </code>
      {widget.voiceEnabled ? null : (
        <p className="mg-card-note">
          {portalText("operations", "turning_voice_on_adds_e21eb302")}<code className="mg-inline-code">{widget.contentSecurityPolicy.voice.join("; ")}</code>.
        </p>
      )}

      <p className="mg-section-title">{portalText("operations", "check_that_it_works_4d3ce334")}</p>
      <ol className="mg-check">
        <li>{portalText("operations", "open_your_site_in_a_private_window_on_a_page_where_the_cha_829f2509")}</li>
        <li>{portalText("operations", "the_bubble_appears_in_the_corner_you_chose_open_it_and_sen_3e10b597")}</li>
        <li>{portalText("operations", "the_conversation_is_listed_in_the_agent_s_history_45a8fbc4")}</li>
        <li>
          {portalText("operations", "no_bubble_open_the_browser_s_console_one_line_starting_wet_a83e1563")}</li>
      </ol>

      <p className="mg-billing">{install.billingSentence}</p>
    </div>
  );
}
